import React, { useRef, useState, useEffect } from 'react';
import * as cls from './faq.module.css';
import { Typography } from '../../../ui/components/typography/typography';
import { Section } from '../../../ui/components/section';

const faqs = [
  {
    question: 'How does Voltaware home energy monitor work?',
    answer:
      'A home energy monitor like Voltaware connects to your fusebox to track energy usage in real time. It uses AI to identify individual appliance consumption and deliver personalised insights through our user-friendly app.',
  },
  {
    question: 'How to monitor energy usage at home?',
    answer:
      'With Voltaware, you can easily monitor your home energy usage by installing our smart sensor and accessing detailed consumption data through our IOS & android app. It breaks down your usage by appliance and provides real-time alerts.',
  },
  {
    question: 'How to monitor home energy usage without a smart meter?',
    answer:
      'Voltaware sensors can be installed directly into your fusebox, so you don’t need a smart meter to benefit from high-resolution energy monitoring. Also it can be used in conjunction with a smart meter.',
  },
  {
    question: 'Can Voltaware help me reduce my electricity bill?',
    answer:
      'Absolutely. By identifying energy-hungry appliances and alerting you to wasteful usage patterns, Voltaware helps you take control and cut down on unnecessary costs.',
  },
  {
    question:
      'Is Voltaware compatible with British Gas or other UK energy providers?',
    answer:
      'Yes, Voltaware works with all single & duel rate energy tariffs on the market, and we have existing partnerships with companies like Octopus Energy and Utilita.',
  },
  {
    question: 'How accurate is Voltaware?',
    answer: (
      <>
        Voltaware delivers appliance-level energy insights with up to 80–85%
        accuracy for energy-hungry appliances like kettles, washing machines,
        and ovens - depending on your home’s setup. Our smart sensor is the most
        accurate of the three inputs (Voltware sensors, smart meter data and
        smart meter data at a higher resolution supplied via consumer access
        devices) to our AI platform, forming the foundation for precise,
        real-time energy tracking. Voltaware helps you identify where energy is
        being wasted so you can take meaningful action.
        <br />
        <br />
        Trusted by Octopus Energy, Utilita, and Enel X, and awarded the Solar
        Impulse Label for Sustainability.
      </>
    ),
  },
];

export const FAQ = ({ customFaqs, shouldAnimate = true }) => {
  const [active, setActive] = React.useState(0);
  const sectionRef = useRef(null);
  const [animatedItems, setAnimatedItems] = useState(new Set());
  const displayedFaqs = customFaqs || faqs;

  useEffect(() => {
    const timeouts = [];

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const faqItems = sectionRef.current?.querySelectorAll(`.${cls.faq}`);
          if (faqItems) {
            faqItems.forEach((item, index) => {
              const timeoutId = setTimeout(() => {
                setAnimatedItems((prev) => new Set(prev).add(index));
              }, index * 400);
              timeouts.push(timeoutId);
            });
          }
        }
      },
      {
        threshold: 0.4,
        rootMargin: '-50px 0px',
      },
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      timeouts.forEach(clearTimeout);

      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  return (
    <div ref={sectionRef}>
      <Section backgroundContrast classNameRoot={cls.section}>
        <Typography as="h2" className={cls['section__title']} tag="h3">
          FAQs
        </Typography>

        {displayedFaqs.map((faq, index) => (
          <button
            className={`${cls.faq} ${active === index ? cls.active : ''} ${animatedItems.has(index) || !shouldAnimate ? cls.animated : cls.hidden}`}
            key={index}
            onClick={() => setActive(index)}
          >
            <Typography as="p" breakLine className={cls.question} tag="span">
              <b>{faq.question}</b>
              <span>{active === index ? '-' : '+'}</span>
            </Typography>
            <Typography as="p" breakLine className={cls.answer} tag="span">
              {faq.answer}
            </Typography>
          </button>
        ))}
      </Section>
    </div>
  );
};
